import React, { useEffect } from 'react'
import { Box, styled } from '@mui/material'

export type ProfileSection = 'overview' | 'galleries' | 'connections' | 'about'

const SECTIONS: { id: ProfileSection; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'galleries', label: 'Galleries' },
  { id: 'connections', label: 'Connections' },
  { id: 'about', label: 'About' },
]

const Nav = styled(Box)({
  position: 'sticky',
  top: 60,
  zIndex: 30,
  display: 'flex',
  gap: 4,
  padding: '18px 28px 14px',
  marginTop: 18,
  background: 'linear-gradient(var(--ds-surface) 72%, transparent)',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const Tab = styled('button', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  height: 32,
  padding: '0 15px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 620,
  color: $active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink-subtle)',
  background: $active ? 'var(--ds-ink)' : 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 140ms ease',
  '&:hover': { color: $active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink)' },
}))

export interface ProfileSubNavProps {
  active: ProfileSection
  onChange: (section: ProfileSection) => void
  overviewRef: React.RefObject<HTMLElement | null>
  galleriesRef: React.RefObject<HTMLElement | null>
  connectionsRef: React.RefObject<HTMLElement | null>
  aboutRef: React.RefObject<HTMLElement | null>
}

export function ProfileSubNav({
  active,
  onChange,
  overviewRef,
  galleriesRef,
  connectionsRef,
  aboutRef,
}: ProfileSubNavProps) {
  const refs: Record<ProfileSection, React.RefObject<HTMLElement | null>> = {
    overview: overviewRef,
    galleries: galleriesRef,
    connections: connectionsRef,
    about: aboutRef,
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section') as ProfileSection
            if (id) onChange(id)
          }
        }
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    )

    for (const [, ref] of Object.entries(refs)) {
      if (ref.current) observer.observe(ref.current)
    }
    return () => observer.disconnect()
  }, [overviewRef, galleriesRef, connectionsRef, aboutRef])

  function scrollTo(id: ProfileSection) {
    refs[id].current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    onChange(id)
  }

  return (
    <Nav role='tablist'>
      {SECTIONS.map((s) => (
        <Tab key={s.id} $active={active === s.id} role='tab' onClick={() => scrollTo(s.id)}>
          {s.label}
        </Tab>
      ))}
    </Nav>
  )
}
